import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import { generateTransformerSchema } from '../../utils/seo';
import { Transformer } from '../../types/transformer';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  breadcrumbs?: Array<{ name: string; item: string }>;
  organization?: {
    name: string;
    url: string;
    logo: string;
    description: string;
  };
  product?: any;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg',
  type = 'website',
  breadcrumbs,
  organization,
  product,
}) => {
  const location = useLocation();
  const siteName = 'Проминнова';
  const VITE_SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://transtsl.ru';
  const fullTitle = `${title} | ${siteName}`;
  const url = `${VITE_SITE_URL}${location.pathname}`;

  // Default organization schema
  const defaultOrganization = {
    '@type': 'Organization',
    '@id': `${VITE_SITE_URL}/#organization`,
    name: organization?.name ?? 'Проминнова',
    url: organization?.url ?? VITE_SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: organization?.logo ?? `${VITE_SITE_URL}/logo.png`,
    },
    description:
      organization?.description ?? 'Поставщик трансформаторов ТСЛ и ТСЗ, КТПН, КСО и кабеля',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Новосибирск',
      addressCountry: 'RU',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+79137701139',
      contactType: 'customer service',
    },
  };

  // Generate breadcrumbs schema
  const breadcrumbsSchema = breadcrumbs
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: `${VITE_SITE_URL}${item.item}`,
        })),
      }
    : null;

  // Generate product schema
  const productSchema = product ? generateTransformerSchema(product) : null;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Open Graph meta tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional meta tags */}
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <link rel="canonical" href={url} />

      {/* Schema.org markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            defaultOrganization,
            {
              '@type': 'WebSite',
              '@id': `${VITE_SITE_URL}/#website`,
              url: VITE_SITE_URL,
              name: siteName,
              description: description,
              publisher: { '@id': `${VITE_SITE_URL}/#organization` },
            },
            {
              '@type': 'WebPage',
              '@id': url,
              url: url,
              name: title,
              description: description,
              isPartOf: { '@id': 'https://prominnova.ru/#website' },
            },
            ...(breadcrumbsSchema ? [breadcrumbsSchema] : []),
            ...(productSchema ? [productSchema] : []),
          ],
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
